/** Synthesised Asteroids sound effects for the hidden arcade (C). */
const SILENT = 0.0001;

function defaultAudioContextFactory() {
    if (typeof window === 'undefined') {
        return null;
    }
    const Ctor = window.AudioContext || window.webkitAudioContext;
    return Ctor ? new Ctor() : null;
}

export function createAsteroidsSound({ audioContextFactory = defaultAudioContextFactory, muted = false } = {}) {
    let ctx = null;
    let triedFactory = false;
    let isMuted = !!muted;
    let disposed = false;

    function tone({
        frequency, endFrequency = null, duration = 0.1, at = 0, type = 'square', gainValue = 0.1,
    }) {
        const start = (ctx.currentTime || 0) + at;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(gainValue, start);
        gain.gain.exponentialRampToValueAtTime(SILENT, start + duration);
        gain.connect(ctx.destination);
        const osc = ctx.createOscillator();
        osc.type = type;
        osc.frequency.setValueAtTime(frequency, start);
        if (endFrequency) {
            osc.frequency.exponentialRampToValueAtTime(endFrequency, start + duration);
        }
        osc.connect(gain);
        osc.start(start);
        osc.stop(start + duration + 0.02);
    }

    function sequence(frequencies, options = {}) {
        frequencies.forEach((frequency, index) => tone({
            frequency, at: index * (options.gap || 0.09), ...options,
        }));
    }

    const effects = {
        start: () => sequence([220, 330, 440], { duration: 0.1 }),
        shoot: () => tone({
            frequency: 900, endFrequency: 280, duration: 0.08, type: 'square', gainValue: 0.07,
        }),
        asteroidHit: event => tone({
            frequency: event.size === 3 ? 90 : event.size === 2 ? 140 : 220,
            duration: 0.12,
            type: 'sawtooth',
            gainValue: 0.13,
        }),
        shipHit: () => sequence([360, 240, 120], {
            duration: 0.22, gap: 0.14, type: 'sawtooth', gainValue: 0.16,
        }),
        waveCleared: () => sequence([440, 660, 880, 1100], { duration: 0.1, gap: 0.08, type: 'triangle' }),
        gameOver: () => sequence([260, 195, 130], {
            duration: 0.25, gap: 0.2, type: 'sawtooth', gainValue: 0.16,
        }),
    };

    return {
        unlock() {
            if (disposed) {
                return;
            }
            if (!ctx && !triedFactory) {
                triedFactory = true;
                ctx = audioContextFactory() || null;
            }
            if (ctx && ctx.state !== 'running' && typeof ctx.resume === 'function') {
                const resumed = ctx.resume();
                if (resumed && typeof resumed.catch === 'function') {
                    resumed.catch(() => {});
                }
            }
        },
        play(event) {
            if (!ctx || disposed || isMuted || !event || !effects[event.type]) {
                return;
            }
            effects[event.type](event);
        },
        setMuted(value) {
            isMuted = !!value;
        },
        isMuted() {
            return isMuted;
        },
        dispose() {
            if (ctx && typeof ctx.close === 'function') {
                const closed = ctx.close();
                if (closed && typeof closed.catch === 'function') {
                    closed.catch(() => {});
                }
            }
            disposed = true;
        },
    };
}
